import {h, Fragment} from "preact"
import { useEffect, useState, useRef, useMemo } from "preact/hooks";
import { useFacet, useAppState } from "@nosto/preact"

import Checkbox from "./elements/Checkbox"
import ColorFilter from "./elements/ColorFilter"
import { useToggleAllFilters } from "./hooks/useToggleAllFilters"

export default function Facet({ facet, expandedFacet }) {
    const { selectedFiltersCount, toggleProductFilter } = useFacet(facet);
    const { toggleAllFilters } = useToggleAllFilters();
    const appState = useAppState();
    const facetLabel = useMemo(() => facet.field.replaceAll('.', '-').replaceAll(' ', '-'), []);
    const [ active, setActive ] = useState(false);

    const [ dropdownHeight, setDropdownHeight ] = useState(null);
    const dropdownRef = useRef(null);

    const allSelected = useMemo(() => facet.data?.every(item => item.selected) ?? false, [facet.data]);

    const totalCount = useMemo(() => {
        return facet.data?.reduce((sum, item) => sum + (item.count || 0), 0) ?? 0;
    }, [facet.data]);

    const toggleSelectAll = async (e) => {
        e.preventDefault();
        const shouldSelectAll = !allSelected;
        const allValues = facet.data?.map(item => item.value) || [];
        const currentFilters = appState?.query?.products?.filter || [];
        await toggleAllFilters(facet.field, allValues, shouldSelectAll, currentFilters);
    };

    useEffect(() => {
        if(window.innerWidth >= 1024 && expandedFacet === facetLabel) {
            setActive(true);
        }
        return () => setActive(false)
    }, [ expandedFacet ])

    useEffect(() => {
        if(dropdownRef?.current && facet?.data?.length > 0) {
            setDropdownHeight(dropdownRef.current.offsetHeight + 20)
        }
    }, [ dropdownRef, facet?.data?.length ]);

    const toggleActive = () => {
        setActive(prevActive => !prevActive);
    }

    return (
        <li class={`ns-facet-dropdown relative px-5 ${active ? "ns-active" : ""}`}>
            <button
                className="ns-facet-body_toggle flex w-full h6--medium block cursor-pointer py-5 relative after:block after:absolute after:duration-700 after:border-gray-900 after:right-7 after:top-1/2 after:-translate-y-1/2 after:border-b after:w-2 after:h-2 after:border-l after:-rotate-45 after:origin-center"
                type="button"
                data-nosto-element="facet"
                aria-controls={`ns-facet-${facetLabel}`}
                aria-label={`Expand ${facet.name} filter options`}
                aria-expanded={active}
                onClick={toggleActive}
                tabIndex="0"
            >
                {facet.name}
                {selectedFiltersCount > 0 && (
                    <span className="ml-1 !bg-transparent !shadow-none">
                        ({selectedFiltersCount})
                    </span>
                )}
            </button>
            <div
                className="ns-facet-body"
                style={`--dropdown-height: ${dropdownHeight}px;`}
                id={`ns-facet-${facetLabel}`}
                aria-hidden={!active}
                aria-labelledby={`ns-facet-heading-${facetLabel}`}
                role="region"
            >
                <div ref={dropdownRef}>
                    <h4
                        className="visually-hidden sr-only"
                        id={`ns-facet-heading-${facetLabel}`} 
                    >
                        Filter options for {facet.name}
                    </h4>
                    <ul className="m-0 p-0">
                        <li
                            className="mb-5 flex items-center gap-x-2.5"
                            data-nosto-element="facet-setting"
                        >
                            <Checkbox
                                identifier={`${facet.id}-filter-select-all`}
                                value="Select all"
                                selected={allSelected}
                                count={totalCount}
                                onChange={toggleSelectAll}
                            />
                        </li>
                        {facet.data?.map((value, index) => (
                            <li
                                key={value}
                                className="mb-5 flex items-center gap-x-2.5"
                                data-nosto-element="facet-setting"
                            >
                                {(facet.name === 'Color') ? (
                                    <ColorFilter
                                        identifier={`${facet.id}-filter-${index}`}
                                        value={value.value}
                                        selected={value.selected}
                                        count={value.count}
                                        onChange={e => {
                                            e.preventDefault()
                                            toggleProductFilter(facet.field, value.value, !value.selected)
                                        }}
                                    />
                                ) : (
                                    <Checkbox
                                        identifier={`${facet.id}-filter-${index}`}
                                        value={value.value}
                                        selected={value.selected}
                                        count={value.count}
                                        onChange={e => {
                                            e.preventDefault()
                                            toggleProductFilter(facet.field, value.value, !value.selected)
                                        }}
                                    />
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </li>
    )
}
